import { Component } from "react";

/**
 * ErrorBoundary — last-line-of-defence for rendering crashes.
 *
 * The July 7 2026 production outage was caused by a single component
 * throwing `ReferenceError: founderMedia is not defined`. Because
 * React error boundaries only exist above the throwing component,
 * and none were present, the entire `<div id="root">` unmounted →
 * black screen on stoicos.ai. Any component crash could have caused
 * the same outage.
 *
 * This boundary wraps the whole Routes tree. When a descendant
 * throws during render, we swap the sub-tree with a minimal,
 * self-contained fallback UI (no external CSS classes, no i18n, no
 * Router, no fetch — nothing that itself could crash). The user
 * always sees *something* — never a black screen.
 *
 * We intentionally do NOT catch async errors (event handlers,
 * setTimeout, promises) — React error boundaries can't and that's
 * expected. Those still surface in the console for debugging.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the error in the browser console so field-debugging still
    // works (production sourcemaps stay useful). Also ship to analytics
    // if a global exception sink exists — try/catch so a broken analytics
    // provider can never itself crash the fallback.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    try {
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "exception", {
          description: `${error?.name || "Error"}: ${error?.message || error}`,
          fatal: true,
        });
      }
    } catch (_) {
      // Analytics unavailable — silent.
    }
  }

  handleReload = () => {
    // Full reload — safer than trying to reset local state, since we
    // don't know how deep the corruption goes.
    if (typeof window !== "undefined") window.location.reload();
  };

  handleBack = () => {
    if (typeof window === "undefined") return;
    // Reset the boundary once the URL has changed, so the previous
    // route renders fresh (SPA back). For full-document navigations the
    // page unloads anyway. Session/localStorage stay untouched.
    window.addEventListener("popstate", () => this.setState({ error: null }), { once: true });
    if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  };

  render() {
    if (!this.state.error) return this.props.children;

    // Inline styles: no dependency on Tailwind, CSS bundles or fonts —
    // so this UI works even if the styling layer itself is what broke.
    const wrap = {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#050505",
      color: "#f4ead5",
      padding: "2rem",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      textAlign: "center",
    };
    const inner = { maxWidth: "560px" };
    const rule = {
      width: "48px",
      height: "1px",
      background: "#c5a059",
      margin: "0 auto 2rem",
      opacity: 0.6,
    };
    const eyebrow = {
      fontSize: "11px",
      letterSpacing: "0.3em",
      textTransform: "uppercase",
      color: "#c5a059",
      marginBottom: "1.25rem",
    };
    const heading = {
      fontSize: "clamp(1.5rem, 4vw, 2rem)",
      fontWeight: 400,
      lineHeight: 1.3,
      margin: "0 0 1rem",
    };
    const body = {
      fontSize: "0.95rem",
      lineHeight: 1.7,
      opacity: 0.75,
      margin: "0 0 2rem",
    };
    const btn = {
      display: "inline-block",
      padding: "0.9rem 2rem",
      backgroundColor: "#c5a059",
      color: "#050505",
      fontSize: "10px",
      letterSpacing: "0.28em",
      textTransform: "uppercase",
      textDecoration: "none",
      border: "none",
      cursor: "pointer",
      margin: "0.35rem",
      minHeight: "44px",
    };
    const btnGhost = {
      ...btn,
      backgroundColor: "transparent",
      color: "#c5a059",
      border: "1px solid rgba(197,160,89,0.6)",
    };

    // No i18n dependency (it might be what crashed) — read the stored
    // site language directly, defaulting to English.
    let nl = false;
    try { nl = window.localStorage.getItem("stoicos_lang") === "nl"; } catch (_) { /* default en */ }
    const txt = nl
      ? {
          eyebrow: "Iets onderbrak het pad",
          heading: "De Stoa is even stil.",
          body: "Een onverwachte fout hield deze pagina tegen. De rest van StoicOs werkt gewoon. Ga terug naar waar je was — je voortgang en login blijven behouden — of herlaad de pagina.",
          back: "← Ga terug waar ik vandaan kwam",
          reload: "Herlaad",
        }
      : {
          eyebrow: "Something interrupted the path",
          heading: "The Stoa is briefly quiet.",
          body: "An unexpected error stopped this page from loading. The rest of StoicOs is unaffected. Go back to where you were — your progress and login are kept — or reload the page.",
          back: "← Take me back to where I was",
          reload: "Reload",
        };

    return (
      <div style={wrap} data-testid="app-error-boundary">
        <div style={inner}>
          <div style={rule} />
          <div style={eyebrow}>{txt.eyebrow}</div>
          <h1 style={heading}>{txt.heading}</h1>
          <p style={body}>{txt.body}</p>
          <button
            type="button"
            onClick={this.handleBack}
            style={btn}
            data-testid="app-error-boundary-back"
          >
            {txt.back}
          </button>
          <button
            type="button"
            onClick={this.handleReload}
            style={btnGhost}
            data-testid="app-error-boundary-reload"
          >
            {txt.reload}
          </button>
        </div>
      </div>
    );
  }
}
